import PropTypes from "prop-types";
import React, { Suspense, useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import LoadingSpinner from "components/Lottie/loading";
import styled, { css, useTheme } from "styled-components";
import Link from "next/link";
import ReactDOMServer from "react-dom/server";
import { find, isEqual, unescape } from "lodash";
import { loadMapsAPI } from "components/MapsApiWrapper";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import LocationIcon from "components/icons/location";
import SearchAreaButton from "components/SearchAreaButton";
import SearchPrevNextButtons from "components/SearchPrevNextButtons";
import SearchCenterMapButton from "components/SearchCenterMapButton";

const GoogleMap = styled.div`
  height: 100%;
  width: 100%;

  ${({ loading }) =>
    loading &&
    css`
      visibility: hidden;
    `}

  /* zoom controls styling */
  .gm-bundled-control {
    div {
      background-color: transparent !important;
      box-shadow: 0 0 0 0 transparent !important;
    }

    .gm-control-active {
      background-color: white !important;
      border-radius: 0.625rem;
      filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.25));
      margin: 0 0 0.5rem !important;

      img {
        height: 0.75rem !important;
        opacity: 0.75;
      }
    }
  }

  /* remove other google labels at bottom of map */
  .gm-style-cc {
    display: none;
  }

  .gm-style-iw-c {
    box-shadow: none;
  }

  .gm-ui-hover-effect {
    display: none !important;
  }

  .gm-style-iw {
    box-shadow: 0rem 0.125rem 0.375rem rgba(138, 168, 181, 0.5);
    padding: 1rem;

    button.Close {
      border: 0.0625rem solid red;
    }
  }
`;

// This styled div is rendered different and doesnt have access to the theme.
// So manually setting all the colors
const MarkerDialogWrapper = styled.div`
  width: 13.125rem;
  text-align: center;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }

  h1,
  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 12.5rem;
    text-align: left;
  }

  h1 {
    font-size: 1.125rem;
    line-height: 1.375rem;
    margin: 1rem 0 0.5rem;
    opacity: 1;
    transition: opacity 0.25s;

    :hover {
      opacity: 0.8;
    }
  }

  span.subtitle {
    align-items: flex-start;
    display: flex;
    margin-bottom: 0.5rem;
    text-align: left;

    svg {
      margin: -0.25rem 0.25rem 0.25rem 0;
    }
  }

  .address {
    display: inline;
    color: #8f978c;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.125rem;
    margin: 0;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 9rem;
  border-radius: 0.75rem;
  overflow: hidden;
  border: solid transparent 0.0625rem;
`;

const Image = styled.image`
  object-fit: cover;
`;

// eslint-disable-next-line no-unused-vars
const MarkerDialog = ({ imageUrl, title, subtitle, friendlyName, urlRegion, urlLocality }) => {
  return (
    <MarkerDialogWrapper>
      <ImageContainer>
        <Image src={imageUrl} />
      </ImageContainer>
      <Link href={`/business/${urlRegion}/${urlLocality}/${friendlyName}`}>
        <a>
          <h1>{unescape(title)}</h1>
        </a>
      </Link>
      {subtitle && (
        <span className="subtitle">
          <LocationIcon />
          <address className="address">{subtitle}</address>
        </span>
      )}
    </MarkerDialogWrapper>
  );
};

MarkerDialog.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  friendlyName: PropTypes.string.isRequired,
  urlLocality: PropTypes.string.isRequired,
  urlRegion: PropTypes.string.isRequired,
};

// convert to an embeddable image for google maps
const locationSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  ReactDOMServer.renderToStaticMarkup(LocationIcon({ color: "#40493D", size: 40, fill: true }))
)}`;

const locationSvgActive = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  ReactDOMServer.renderToStaticMarkup(LocationIcon({ color: "#FF7817", size: 40, fill: true }))
)}`;

function mapDataToMarkers(data, currentLocation) {
  return (
    data?.vendorBusinesses?.data?.map((business) => {
      return {
        title: business.attributes.businessName,
        friendlyName: business.friendlyName,
        businessId: business.id,
        serviceTypes: business?.include?.serviceTypes?.data,
        ...business.attributes,
      };
    }) ||
    (currentLocation
      ? [
          {
            locationCenter: true,
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
          },
        ]
      : [])
  );
}

// https://developers.google.com/maps/documentation/javascript/style-reference
const customMapStyles = [
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.medical",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.attraction",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.school",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.sports_complex",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.place_of_worship",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.government",
    stylers: [{ visibility: "off" }],
  },
];

function MapWithMarkersContent({ data, onMarkerClick, activeBusinessId, currentLocation, loading, services, setActiveBusinessId }) {
  const theme = useTheme();
  const mapEl = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState(mapDataToMarkers(data, currentLocation));
  const [activeMarkers, setActiveMarkers] = useState([]);
  const [showSearchRecenterButtons, setShowSearchRecenterButtons] = useState(false);
  const [showPrevNext, setShowPrevNext] = useState(false);
  const [mapsApi, setMapsApi] = useState(null);
  const router = useRouter();

  // because data is an object, it may cause unnecessary renders if the
  // response data needs to be re-mapped into marker data. this only triggers
  // a re-render if the mapped content changes
  useEffect(() => {
    const nextMarkers = mapDataToMarkers(data, currentLocation);
    if (!isEqual(markers, nextMarkers)) {
      setMarkers(nextMarkers);
    }
  }, [data, markers]);

  useEffect(() => {
    loadMapsAPI(() => {
      setMapsApi(window.google?.maps);
    });
  }, []);

  useEffect(() => {
    if (mapsApi && mapEl.current) {
      const nextMap = new mapsApi.Map(mapEl.current, {
        backgroundColor: theme.themeColors.white,
        center: { lat: parseFloat(currentLocation?.latitude), lng: parseFloat(currentLocation?.longitude) },
        disableDefaultUI: true,
        clickableIcons: false,
        styles: customMapStyles,
        zoomControl: true,
        zoomControlOptions: {
          position: mapsApi.ControlPosition.RIGHT_TOP,
        },
      });

      setMap(nextMap);
    }
  }, [mapsApi]);

  const setActiveMarker = useCallback(() => {
    if (!mapsApi || !map) return;

    // set all markers back to default icon
    activeMarkers.forEach((item) => {
      item.setIcon(locationSvg);
      item?.infoWindow?.close();
    });

    // get the target marker that has the same id as the activeBusinessId
    const targetMarker = find(activeMarkers, { id: activeBusinessId });

    // update selected marker to show active icon
    targetMarker?.setIcon(locationSvgActive);

    // open marker info window
    targetMarker?.infoWindow?.open({
      anchor: targetMarker,
      map,
      shouldFocus: true,
      setActiveBusinessId: activeBusinessId,
      callback: setShowPrevNext(true),
    });

    // click on the map close the open marker info window
    mapsApi.event.addListener(map, "click", () => {
      targetMarker?.setIcon(locationSvg);
      targetMarker?.infoWindow?.close();
      setActiveBusinessId(null);
    });
  }, [map, activeBusinessId, activeMarkers]);

  useEffect(() => {
    if (!map) {
      return;
    }

    // hide the "show search area" button
    setShowSearchRecenterButtons(false);
    mapsApi.event.clearListeners(map, "bounds_changed");

    const bounds = new mapsApi.LatLngBounds();
    const nextActiveMarkers = [];

    // clear markers from map. Unfortunately the only way to clear all markers off a map
    activeMarkers.forEach((item) => {
      item.setMap(null);
      item?.infoWindow?.unbindAll();
    });

    markers.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) {
        return;
      }
      const url = generateRandomImagePlaceholderPath(markerData.businessId, markerData.serviceTypes);
      const position = new mapsApi.LatLng(parseFloat(markerData.latitude), parseFloat(markerData.longitude));

      const googleMarker = new mapsApi.Marker({
        animation: mapsApi.Animation.DROP,
        position,
        map,
        mediumImageUrl: markerData.mediumImageUrl,
        title: markerData.title,
        icon: locationSvg,
        id: markerData.businessId,
        friendlyName: markerData.friendlyName,
        urlRegion: markerData.urlRegion,
        urlLocality: markerData.urlLocality,
        latitude: parseFloat(markerData.latitude),
        longitude: parseFloat(markerData.latitude),
        streetAddress: markerData?.formattedAddress,
      });

      let content;
      if (!markerData.locationCenter) {
        content = ReactDOMServer.renderToStaticMarkup(
          MarkerDialog({
            imageUrl: googleMarker.mediumImageUrl || url,
            title: googleMarker.title,
            subtitle: googleMarker.streetAddress,
            friendlyName: googleMarker.friendlyName,
            urlRegion: googleMarker.urlRegion,
            urlLocality: googleMarker.urlLocality,
            position,
          })
        );
        googleMarker.infoWindow = new mapsApi.InfoWindow({ content });
        // add click event listener for markers
        mapsApi.event.addListener(googleMarker, "click", () => {
          onMarkerClick(markerData.businessId);
        });

        mapsApi.event.addListener(googleMarker.infoWindow, "closeclick", () => {
          onMarkerClick(null);
          googleMarker.setIcon(locationSvg);
        });
      }

      nextActiveMarkers.push(googleMarker);
      setActiveMarkers(nextActiveMarkers);

      if (markerData.locationCenter) {
        const position1 = new mapsApi.LatLng(parseFloat(markerData.latitude) + 0.2, parseFloat(markerData.longitude));
        const position2 = new mapsApi.LatLng(parseFloat(markerData.latitude) - 0.2, parseFloat(markerData.longitude));
        bounds.extend(position1);
        bounds.extend(position2);
      } else {
        bounds.extend(position);
      }
    });

    // set bounds for map based on all markers added
    map.fitBounds(bounds);

    // toggle the "show search area" button when map pans
    // delayed to account for animation
    setTimeout(() => {
      mapsApi.event.addListener(map, "bounds_changed", () => {
        setShowSearchRecenterButtons(true);
      });
    }, 2000);
  }, [markers, map]);

  useEffect(() => {
    setActiveMarker(activeBusinessId);
  }, [activeBusinessId]);

  return (
    <>
      {loading && <LoadingSpinner />}
      {!map?.data?.map?.tilesLoading && (
        <>
          {showSearchRecenterButtons && (
            <>
              <SearchCenterMapButton currentLocation={currentLocation} map={map} />
              <SearchAreaButton map={map} router={router} services={services} />
            </>
          )}
          {showPrevNext && markers.length > 1 && (
            <SearchPrevNextButtons activeBusinessId={activeBusinessId} data={data} setActiveBusinessId={setActiveBusinessId} />
          )}
        </>
      )}
      {!map?.data?.map?.tilesLoading && <GoogleMap ref={mapEl} loading={loading} />}
    </>
  );
}

MapWithMarkersContent.propTypes = {
  currentLocation: PropTypes.object,
  data: PropTypes.object,
  onMarkerClick: PropTypes.func,
  activeBusinessId: PropTypes.string,
  loading: PropTypes.bool,
  services: PropTypes.string,
  setActiveBusinessId: PropTypes.func,
};

MapWithMarkersContent.defaultProps = {
  currentLocation: undefined,
  data: undefined,
  onMarkerClick: undefined,
  activeBusinessId: undefined,
  loading: true,
  services: "",
  setActiveBusinessId: () => {},
};

export default function MapWithMarkers(props) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <MapWithMarkersContent {...props} />
    </Suspense>
  );
}
